import enGB from "../locales/en-GB.json";

/**
 * EN-5191: warns when keys this build knows about did not come back from the API.
 *
 * This exists because of the most expensive failure mode we have: the endpoint
 * (TranslationRepository.GetAllWithFallbackAsync) drives off the **en-GB rows** and LEFT JOINs
 * the requested language. A key with no en-GB row in the database is therefore absent from the
 * response for *every* language — the portal falls back to the bundle compiled into the build
 * and renders English, with a 200, no error and nothing in the console.
 *
 * On screen that is indistinguishable from "the language switch did not work", which is how
 * EN-5191 spent an afternoon looking at language detection, Accept-Language headers and
 * localStorage while the real answer was 274 rows missing from a table.
 *
 * So: one line, naming the count and a sample, whenever the active language is not the
 * baseline. Silent is the only acceptable result of a language switch.
 */
export const UNPUBLISHED_SAMPLE_SIZE = 8;

export interface IUnpublishedKeysReport {
  /** Keys in the shipped bundle that the API did not return at all. */
  missing: string[];
  /** Keys the API returned, but still holding the English value. */
  untranslated: string[];
  total: number;
}

/**
 * Compares the published bundle against the baseline. `published` is what the API returned,
 * already parsed into key -> value.
 */
export function compareAgainstBaseline(
  published: Record<string, unknown>
): IUnpublishedKeysReport {
  const baseline = enGB as Record<string, string>;
  const keys = Object.keys(baseline);

  const missing = keys.filter((key) => !(key in published));
  const untranslated = keys.filter(
    (key) => key in published && published[key] === baseline[key]
  );

  return { missing, untranslated, total: keys.length };
}

function sample(keys: string[]): string {
  return keys.slice(0, UNPUBLISHED_SAMPLE_SIZE).join(", ");
}

/**
 * Logs the report. No-ops for the baseline language, where "missing" and "untranslated" are
 * meaningless, and no-ops when nothing is missing — the quiet case has to stay quiet or the
 * warning gets tuned out and stops working.
 */
export function reportUnpublishedKeys(
  language: string,
  published: Record<string, unknown>,
  baselineLanguage = "en-GB"
): IUnpublishedKeysReport | undefined {
  if (!language || language === baselineLanguage) return undefined;

  const report = compareAgainstBaseline(published);

  if (report.missing.length > 0) {
    // These render in English however complete the language's own rows are: the row set the
    // API returns is defined by en-GB, so the English seed is what has to be fixed first.
    console.warn(
      `[i18n] ${language}: ${report.missing.length} of ${report.total} keys were not returned by the API ` +
        `and will render in English. This means the key has no ${baselineLanguage} row in the ` +
        `translations table — seed ${baselineLanguage} before ${language}. ` +
        `First few: ${sample(report.missing)}`
    );
  }

  if (report.untranslated.length > 0) {
    // Expected and harmless — the API COALESCEs each key over its English row — so this is
    // informational, not a warning. It is the translator's worklist, not a defect.
    console.info(
      `[i18n] ${language}: ${report.untranslated.length} of ${report.total} keys came back with ` +
        `the English value (no ${language} row yet). First few: ${sample(
          report.untranslated
        )}`
    );
  }

  return report;
}
